import React, { useEffect, useState } from "react";
import { useFormContext, useWatch, Controller } from "react-hook-form";
import { get } from "lodash";
import { DatePickerComponent } from "@syncfusion/ej2-react-calendars";
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { Modal } from 'react-bootstrap';
import './sccs/InputTextIntegration.scss';

interface InputTextIntegrationProps {
    name: string;
    placeholder?: string;
    disabled?: boolean;
    readonly?: boolean;
    maxLength?: number;
    defaultValue?: string;
    minWidth?: number | string;
    req?: string;
    pattern?: RegExp;
    patternMessage?: string;
    inputType?: number;
    modal?: string | null;
}

const InputTextIntegration: React.FC<InputTextIntegrationProps> = ({
    name,
    placeholder,
    disabled,
    readonly,
    maxLength,
    defaultValue,
    req,
    pattern,
    patternMessage,
    inputType = 1,
    minWidth = 120,
    modal = null,
}) => {

    const {
        register,
        control,
        formState: { errors },
        setValue,
    } = useFormContext();

    const value = useWatch({ name });
    const errorMessage = get(errors, name)?.message;
    const [showModal, setShowModal] = useState(false);
    const controlMinWidth =
        typeof minWidth === "number" ? `${minWidth}px` : (minWidth ?? "120px");


    useEffect(() => {
        if (
            inputType === 4 &&
            typeof value === "string" &&
            value.includes("T")
        ) {
            const normalized = value.split("T")[0];

            setValue(name, normalized, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
            });
        }
    }, [value, inputType, name, setValue]);

    // Default value
    useEffect(() => {
        if (defaultValue !== undefined && defaultValue !== "") {
            setValue(name, defaultValue);
        }
    }, []);

    const getRegisterOptions = () => {
        switch (inputType) {
            case 2:
                return {
                    required: req,
                    setValueAs: (v: any) => {
                        if (v === "") return v;
                        return /^[0-9]+$/.test(v) ? parseInt(v, 10) : v;
                    },
                    pattern: {
                        value: /^[0-9]+$/,
                        message: "Solo se admiten números enteros",
                    },
                };

            case 3:
                return {
                    required: req,
                    setValueAs: (v: any) => {
                        const str = typeof v === "string" ? v : String(v ?? "");
                        return /^-?[0-9]+([.,][0-9]+)?$/.test(str)
                            ? parseFloat(str.replace(",", "."))
                            : v;
                    },
                    pattern: {
                        value: /^-?[0-9]+([.,][0-9]+)?$/,
                        message:
                            patternMessage ||
                            "Introduce un número válido (entero o decimal)",
                    },
                };

            case 5:
                return {
                    required: req,
                    setValueAs: (v: any) => {
                        if (v === "") return v;
                        return /^[0-9]{4}$/.test(v) ? parseInt(v, 10) : v;
                    },
                    pattern: {
                        value: /^[0-9]{4}$/,
                        message: "Introduce un año válido (4 dígitos)",
                    },
                };

            case 1:
            default:
                return {
                    required: req,
                    pattern: pattern
                        ? { value: pattern, message: patternMessage }
                        : undefined,
                };
        }
    };

    return (
        <span
            className="input-text-integration-wrapper"
            data-input-name={inputType === 4 ? name : undefined}
            style={{
                cursor: disabled ? "not-allowed" : "text"
            }}
        >
            <span className="input-text-integration-inline">

                {modal && (
                    <TooltipComponent content="Más Información">
                        <div
                            onClick={() => setShowModal(true)}
                            className="information-icon-orange p-0"
                            style={{ cursor: 'pointer' }}
                        />
                    </TooltipComponent>
                )}

                {inputType === 4 ? (
                    <Controller
                        name={name}
                        
                        control={control}
                        rules={{ required: req }}
                        render={({ field }) => (
                            <DatePickerComponent
                                id={name}
                                name={name}
                                htmlAttributes={{
                                    name,
                                    "data-input-name": name,
                                }}
                                value={
                                    field.value
                                        ? (typeof field.value === "string"
                                            ? new Date(field.value)
                                            : field.value)
                                        : null
                                }
                                change={(args) => {
                                    const date = args.value as Date | null;

                                    if (!date) {
                                        field.onChange(null);
                                        return;
                                    }


                                    const year = date.getFullYear();
                                    const month = String(date.getMonth() + 1).padStart(2, "0");
                                    const day = String(date.getDate()).padStart(2, "0");

                                    const formatted = `${year}-${month}-${day}T00:00:00`;

                                    field.onChange(formatted);
                                }}
                                format="dd-MM-yyyy"
                                locale="es"
                                firstDayOfWeek={1}
                                placeholder={placeholder || "dd-mm-aaaa"}
                                disabled={disabled}
                                width={controlMinWidth}
                                cssClass={`input-text-integration ${errorMessage ? "input-text-integration--error" : ""}`}
                            />
                        )}
                    />
                ) : (
                    <input
                        id={name}
                        data-input-name={name}
                        type="text"
                        placeholder={placeholder}
                        maxLength={maxLength}
                        readOnly={readonly || false}
                        disabled={disabled}
                        autoComplete='off'
                        spellCheck={false}
                        autoCapitalize='off'
                        autoCorrect='off'
                        className={`input-text-integration ${errorMessage ? "input-text-integration--error" : ""}`}
                        style={{
                            cursor: disabled ? "not-allowed" : "text",
                            minWidth: controlMinWidth,
                        }}
                        {...register(name, getRegisterOptions())}
                    />
                )}
            </span>

            {errorMessage && (
                <span className="input-text-integration-error">
                    {errorMessage as any}
                </span>
            )}

            <Modal size='lg' show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{placeholder || 'Información'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modal}</Modal.Body>
            </Modal>
        </span>
    );
};

export default InputTextIntegration;